/**
 * Dossier intake (BRIEF product scope 1).
 *
 * Drag-and-drop multi-file PDF upload plus Gemeinde / Bautyp / Verfahren, then
 * a run on the uploaded dossier.
 *
 * The attestation is a gate, not a formality: the submit button is disabled
 * until the checkbox is ticked, the checkbox is never pre-ticked, and the flag
 * travels to the server, which records it in the audit trail. It is required in
 * EVERY data mode, not only in `anonymised` — a gate that a mode switch turns
 * off is not a gate, and being stricter than the brief here costs one click.
 *
 * What the sentence SAYS is not the same in every mode, and since 2026-08-20 it
 * cannot be: in a `live` workspace "contains no real personal data" is false,
 * and asking somebody to tick a false sentence is worse than asking nothing. So
 * the text follows the workspace (`attestationKey`) and names what actually
 * happens to the files — including, where the server is configured that way,
 * that the extraction step goes to Anthropic in the United States. The German
 * original of that same sentence arrives as `workspace.attestation_de` and is
 * what the server writes into the audit trail.
 *
 * The one thing this screen cannot promise is that the attestation is true. It
 * is not verifiable, and the brief says so: attestation + audit trail + the
 * server-side data mode is the designed containment. No extra magic on top.
 */
import { useState } from 'react'
import { FileText, Trash2, Upload, X } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  MAX_FILES,
  MAX_FILE_BYTES,
  MAX_PAGES,
  MAX_TOTAL_BYTES,
  admit,
  formatBytes,
  type Candidate,
  type RejectReason,
} from '@/features/upload/uploadRules'
import { DraftNote } from '@/features/rulebase/DraftBadge'
import { isDraft } from '@/features/rulebase/rulebase'
import {
  DATA_MODE_LABEL,
  attestationHelpKey,
  attestationKey,
  type DataMode,
} from '@/features/workspace/dataModes'
import { useI18n, type TranslationKey } from '@/i18n'
import { Link, buildPath, navigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'
import type { GemeindePack, WorkspaceRef } from '@/api/types'

const REJECT_LABEL: Record<RejectReason, TranslationKey> = {
  notPdf: 'upload.reject.notPdf',
  tooLarge: 'upload.reject.tooLarge',
  totalTooLarge: 'upload.reject.totalTooLarge',
  tooMany: 'upload.reject.tooMany',
  duplicate: 'upload.reject.duplicate',
  empty: 'upload.reject.empty',
}

/** Kept in the frontend because they are form vocabulary, not rulebase data. */
const BAUTYPEN = ['neubau', 'umbau', 'anbau', 'aufstockung', 'abbruch-wiederaufbau'] as const
const VERFAHREN = ['ordentlich', 'vereinfacht', 'anzeigeverfahren'] as const

export function UploadScreen({ workspace }: { workspace: WorkspaceRef }) {
  const { t } = useI18n()
  const gemeinden = useAsync<GemeindePack[]>((signal) => api.gemeinden(workspace.id, signal))

  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [gemeinde, setGemeinde] = useState('')
  const [bautyp, setBautyp] = useState<string>(BAUTYPEN[0])
  const [verfahren, setVerfahren] = useState<string>(VERFAHREN[0])
  const [zone, setZone] = useState('')
  const [title, setTitle] = useState('')
  const [attested, setAttested] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedPack =
    (gemeinden.data ?? []).find((pack) => pack.gemeinde === gemeinde) ?? null
  const accepted = candidates.filter((candidate) => candidate.reason === null)
  const rejected = candidates.filter((candidate) => candidate.reason !== null)
  const totalBytes = accepted.reduce((sum, candidate) => sum + candidate.file.size, 0)

  const add = (files: FileList | null) => {
    if (!files || files.length === 0) return
    void admit(candidates, Array.from(files)).then(setCandidates)
  }

  const remove = (key: string) =>
    setCandidates((current) => current.filter((candidate) => candidate.key !== key))

  const ready = accepted.length > 0 && gemeinde !== '' && attested && !busy

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      const uploaded = await api.uploadDossier(workspace.id, {
        files: accepted.map((candidate) => candidate.file),
        gemeinde,
        bautyp,
        verfahren,
        zone: zone.trim(),
        title: title.trim() || accepted[0].file.name,
        attested,
      })
      // The intake endpoint starts the run in the same request; if it ever
      // stops doing so, the dossier still exists and the run is one call away.
      const run = uploaded.run ?? (await api.createRun(uploaded.dossier.id, workspace.id))
      navigate(buildPath('run', { runId: run.run_id }))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="flex max-w-[900px] flex-col gap-8" onSubmit={(e) => void submit(e)}>
      <section className="flex flex-col gap-3">
        <h1 className="font-display text-[32px] leading-tight font-semibold">{t('upload.title')}</h1>
        <p className="max-w-[70ch] text-[15px] text-muted">{t('upload.lead')}</p>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>{t('upload.filesTitle')}</CardTitle>
          <CardDescription>
            {t('upload.caps', {
              files: MAX_FILES,
              perFile: formatBytes(MAX_FILE_BYTES),
              total: formatBytes(MAX_TOTAL_BYTES),
              pages: MAX_PAGES,
            })}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div
            data-testid="dropzone"
            data-dragging={dragging}
            onDragOver={(event) => {
              event.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault()
              setDragging(false)
              add(event.dataTransfer.files)
            }}
            className={[
              'flex flex-col gap-3 rounded-md border border-dashed px-6 py-8 transition-colors duration-fast ease-enter',
              dragging ? 'border-accent bg-accent-soft' : 'border-border-strong bg-surface',
            ].join(' ')}
          >
            <p className="flex items-center gap-2 text-[14px] font-medium text-ink">
              <Icon icon={Upload} />
              {t('upload.dropHere')}
            </p>
            <label htmlFor="dossier-files" className="text-[13px] text-muted">
              {t('upload.orChoose')}
            </label>
            <input
              id="dossier-files"
              type="file"
              multiple
              accept="application/pdf,.pdf"
              onChange={(event) => {
                add(event.target.files)
                // Allow re-picking the same file after removing it.
                event.target.value = ''
              }}
              className="block w-full text-[13px] text-ink file:me-3 file:rounded-sm file:border file:border-border-strong file:bg-white file:px-3 file:py-1.5 file:text-[13px] file:text-ink"
            />
          </div>

          <ul className="flex flex-col" data-testid="file-list">
            {candidates.map((candidate) => (
              <li
                key={candidate.key}
                data-file-state={candidate.reason ?? 'accepted'}
                className="flex flex-wrap items-center gap-3 border-b border-border py-2 last:border-b-0"
              >
                <Icon
                  icon={candidate.reason ? X : FileText}
                  className={candidate.reason ? 'text-signal-red-text' : 'text-accent'}
                />
                <span className="text-[13px] text-ink">{candidate.file.name}</span>
                <span className="num text-[12px] text-muted">{formatBytes(candidate.file.size)}</span>
                {candidate.reason ? (
                  <span className="text-[12px] text-signal-red-text">
                    {t(REJECT_LABEL[candidate.reason], {
                      max: formatBytes(MAX_FILE_BYTES),
                      total: formatBytes(MAX_TOTAL_BYTES),
                      files: MAX_FILES,
                    })}
                  </span>
                ) : (
                  <Badge variant="neutral">{t('upload.accepted')}</Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="ms-auto"
                  onClick={() => remove(candidate.key)}
                  aria-label={t('upload.removeFile', { name: candidate.file.name })}
                >
                  <Icon icon={Trash2} />
                </Button>
              </li>
            ))}
            {candidates.length === 0 ? (
              <li className="py-2 text-[13px] text-muted">{t('upload.noFiles')}</li>
            ) : null}
          </ul>

          <p role="status" className="text-[12px] text-muted">
            {t('upload.summary', {
              accepted: accepted.length,
              rejected: rejected.length,
              size: formatBytes(totalBytes),
            })}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('upload.contextTitle')}</CardTitle>
          <CardDescription>{t('upload.contextLead')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Field id="upload-gemeinde" label={t('upload.gemeinde')}>
              <select
                id="upload-gemeinde"
                required
                value={gemeinde}
                onChange={(event) => setGemeinde(event.target.value)}
                className="h-10 w-full min-w-0 rounded-sm border border-border-strong bg-white px-2 text-[15px] text-ink"
              >
                <option value="">{t('upload.pleaseChoose')}</option>
                {(gemeinden.data ?? []).map((pack) => (
                  <option key={pack.gemeinde} value={pack.gemeinde}>
                    {pack.gemeinde}
                    {/* A `<select>` cannot carry a badge, so the label carries
                        the word. The dashed treatment follows underneath the
                        moment a draft Gemeinde is picked. */}
                    {isDraft(pack) ? ` · ${t('rulebase.draftBadge')}` : ''}
                  </option>
                ))}
              </select>
            </Field>

            <Field id="upload-bautyp" label={t('upload.bautyp')}>
              <select
                id="upload-bautyp"
                value={bautyp}
                onChange={(event) => setBautyp(event.target.value)}
                className="h-10 w-full min-w-0 rounded-sm border border-border-strong bg-white px-2 text-[15px] text-ink"
              >
                {BAUTYPEN.map((value) => (
                  <option key={value} value={value}>
                    {t(`upload.bautyp.${value}` as TranslationKey)}
                  </option>
                ))}
              </select>
            </Field>

            <Field id="upload-verfahren" label={t('upload.verfahren')}>
              <select
                id="upload-verfahren"
                value={verfahren}
                onChange={(event) => setVerfahren(event.target.value)}
                className="h-10 w-full min-w-0 rounded-sm border border-border-strong bg-white px-2 text-[15px] text-ink"
              >
                {VERFAHREN.map((value) => (
                  <option key={value} value={value}>
                    {t(`upload.verfahren.${value}` as TranslationKey)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Optional on purpose: the zone is read from the Baugesuch. It is
              offered because a scan the extractor cannot read otherwise dead-
              ends the run at "Fakten ohne 'zone'" — a value the architect knows
              off the top of their head should not cost them a whole run. */}
          <Field id="upload-zone" label={t('upload.zone')}>
            <input
              id="upload-zone"
              type="text"
              value={zone}
              onChange={(event) => setZone(event.target.value)}
              placeholder={t('upload.zonePlaceholder')}
              className="h-10 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink placeholder:text-muted-2"
            />
          </Field>

          <Field id="upload-title" label={t('upload.dossierTitle')}>
            <input
              id="upload-title"
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t('upload.dossierTitlePlaceholder')}
              className="h-10 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink placeholder:text-muted-2"
            />
          </Field>

          {/* M4 — indicative mode at the point of choice, not after the run.
              Picking a draft Gemeinde is allowed; being surprised by it later
              is not. */}
          {selectedPack && isDraft(selectedPack) ? (
            <DraftNote gemeinde={selectedPack.gemeinde} />
          ) : null}

          {gemeinden.error ? (
            <ErrorNote message={gemeinden.error} onRetry={gemeinden.reload} retryLabel={t('common.retry')} />
          ) : null}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-[12px] text-muted">{t('upload.missingGemeinde')}</p>
            <Link
              to={buildPath('rulebaseIngest', { workspaceId: workspace.id })}
              className="text-[12px] text-ink underline underline-offset-2"
            >
              {t('rulebase.ingestCta')}
            </Link>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('upload.attestTitle')}</CardTitle>
          <CardDescription>{t('upload.attestLead')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <label className="flex items-start gap-3 rounded-sm border border-border bg-surface px-4 py-3">
            <input
              type="checkbox"
              checked={attested}
              onChange={(event) => setAttested(event.target.checked)}
              aria-describedby="attest-help"
              className="mt-1 accent-accent"
            />
            <span className="text-[14px] text-ink" data-testid="attest-text">
              {t(attestationKey(workspace))}
            </span>
          </label>
          <p id="attest-help" className="text-[12px] text-muted">
            {t(attestationHelpKey(workspace), {
              mode: t(DATA_MODE_LABEL[workspace.data_mode as DataMode] ?? 'workspace.mode.unknown'),
            })}
          </p>

          {error ? <ErrorNote message={error} /> : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={!ready}>
              <Icon icon={Upload} />
              {busy ? t('upload.submitting') : t('upload.submit')}
            </Button>
            {!ready && !busy ? (
              <span className="text-[12px] text-muted" data-testid="upload-blocked">
                {!attested ? t('upload.blockedAttest') : null}
                {attested && accepted.length === 0 ? t('upload.blockedFiles') : null}
                {attested && accepted.length > 0 && gemeinde === '' ? t('upload.blockedGemeinde') : null}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </form>
  )
}

function Field({
  id,
  label,
  children,
}: {
  id: string
  label: string
  children: React.ReactNode
}) {
  return (
    // `min-w-0`: a <select> is as wide as its longest option unless the grid
    // cell is allowed to shrink — that was the 320 CSS px overflow on this
    // screen (WCAG 2.2 §1.4.10 Reflow).
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium text-ink-2">
        {label}
      </label>
      {children}
    </div>
  )
}
